import { sendEvent } from '@/tracking/gbanalytics'
import { getContentCountsForUser } from '@/api/dynamodb'
import { ACTION_TYPES, CONTENT_TYPES, savedContentTypeMapping } from '@/util/access_visibility'
import { getUserDownloads, saveUserDownload } from '@/api/downloads'

export default {
  namespaced: true,
  state: {
    content_access_report_error: null,
    content_access_report: {
      toolkit_resource_download_count: null,
      toolkit_resource_download_quota: null,
      toolkit_copy_count: null,
      toolkit_copy_quota: null,
      toolkit_print_count: null,
      toolkit_print_quota: null
    },
    downloaded_uuids: [],

    // hardcoded defaults which should be replaced when we load user info during initialization
    libraryQuotas: {
      maxStudents: 50,
      maxGoalsPerStudent: 15,
      maxStrategiesPerStudent: 15,
      maxPresentlevelsPerStudent: 5,
      maxResourcesPerStudent: 20,
      shouldEnforce: true
    },
    refetchTimeout: null
  },
  mutations: {
    setContentAccessReport(state, report) {
      if (!report) {
        return
      }
      if (report.error) {
        state.content_access_report_error = report.response || 'error fetching report'
      } else {
        state.content_access_report_error = null
      }
      state.content_access_report.toolkit_copy_count = report.toolkit_copy_count || 0
      state.content_access_report.toolkit_copy_quota = report.toolkit_copy_quota || 0
      state.content_access_report.toolkit_print_count = report.toolkit_print_count || 0
      state.content_access_report.toolkit_print_quota = report.toolkit_print_quota || 0
      state.content_access_report.toolkit_resource_download_count = report.toolkit_resource_download_count || 0
      state.content_access_report.toolkit_resource_download_quota = report.toolkit_resource_download_quota || 0
    },
    incrementCopyCount(state) {
      state.content_access_report.toolkit_copy_count += 1
    },
    incrementDownloadCount(state) {
      state.content_access_report.toolkit_resource_download_count += 1
    },
    incrementPrintCount(state) {
      state.content_access_report.toolkit_print_count += 1
    },
    setDownloadedUuids(state, list) {
      state.downloaded_uuids = list || []
    },
    addDownloadedUuid(state, uuid) {
      state.downloaded_uuids = state.downloaded_uuids.concat([uuid])
    },
    setLibraryQuotasFromTkAccount(state, tkAcc) {
      if (!tkAcc) return

      state.libraryQuotas.maxStudents = tkAcc.max_students
      state.libraryQuotas.maxGoalsPerStudent = tkAcc.max_goals_per_student
      state.libraryQuotas.maxStrategiesPerStudent = tkAcc.max_strategies_per_student
      state.libraryQuotas.maxResourcesPerStudent = tkAcc.max_resources_per_student
      state.libraryQuotas.maxPresentlevelsPerStudent = tkAcc.max_presentlevels_per_student
      state.libraryQuotas.shouldEnforce = tkAcc.enforce_library_quotas
    },
    setRefetchTimeout(state, timeout) {
      state.refetchTimeout = timeout
    }
  },
  actions: {
    trackPrint(
      { commit, dispatch, getters, rootGetters },
      { contentType, contentTitle, contentUuid, otherData }
    ) {
      let data = Object.assign(otherData, {
        content_type: contentType,
        content_title: contentTitle,
        content_uuid: contentUuid,
        print_count: getters.printCount
      })
      if (getters.contentAccessStatus.reportError) {
        data.content_access_report_error = getters.contentAccessStatus.reportError
      }
      let contentInfo = {
        cu: contentUuid,
        ct: contentTitle,
        ctype: contentType
      }
      sendEvent('Printed Content', data, {
        ...contentInfo,
        ...rootGetters['user/eventMetadata']
      })
      commit('incrementPrintCount')
      dispatch('refetchContentAccessReport')
    },

    trackDownload({ commit, dispatch, getters, rootGetters }, { resource, link }) {
      let isRedownload = getters.redownloadMap[resource.uuid] || false
      if (!isRedownload) {
        commit('incrementDownloadCount')
        commit('addDownloadedUuid', resource.uuid)
      }

      // persist to download history collection
      if (rootGetters['user/isAuthenticated']) {
        saveUserDownload({ content_type: 'resource', content_uuid: resource.uuid })
      }
      dispatch('refetchContentAccessReport')
    },

    /* payload should have the following fields:
     *  - actionType
     *  - contentType
     *  - contentInfo
     */
    sendContentAccessBlockedQuota({ getters, rootGetters }, { actionType, contentInfo }) {
      let data = {}
      switch (actionType) {
        case ACTION_TYPES.DOWNLOAD:
          data.title = contentInfo.ct
          data.resource_uuid = contentInfo.cu
          data.blocked_action = 'Download Resource'
          data.blocking_quota = 'toolkit_resource_download_quota'
          data.blocking_count = getters.downloadCount
          data.blocking_quota_value = getters.downloadQuota
          break
        case ACTION_TYPES.COPY:
          data.blocked_action = 'Copy Text'
          data.blocking_quota = 'toolkit_copy_quota'
          data.blocking_count = getters.copyCount
          data.blocking_quota_value = getters.copyQuota
          break
        case ACTION_TYPES.PRINT:
          data.blocked_action = 'Print Content'
          data.blocking_quota = 'toolkit_print_quota'
          data.blocking_count = getters.printCount
          data.blocking_quota_value = getters.printQuota
          data.content_type = contentInfo.ctype
          data.title = contentInfo.ct
          break
        default:
          return
      }

      if (getters.contentAccessStatus.reportError) {
        data.content_access_report_error = getters.contentAccessStatus.reportError
      }

      sendEvent('Content Access Blocked', data, {
        ...contentInfo,
        ...rootGetters['user/eventMetadata']
      })
    },

    // gets user's download history
    getUserDownloads({ commit, rootGetters }) {
      if (rootGetters['user/isAuthenticated']) {
        getUserDownloads({}).then(result => {
          if (result.collection && result.collection.content) {
            commit('setDownloadedUuids', result.collection.content.map(item => item.content_uuid))
          }
        })
      }
    },

    // refetches the content access report
    refetchContentAccessReport({ state, commit, rootGetters }) {
      var timeout = state.refetchTimeout
      if (timeout) {
        clearTimeout(timeout)
      }
      commit('setRefetchTimeout', setTimeout(() => {
        getContentCountsForUser(rootGetters['user/userId']).then(data => {
          if (data) {
            const updatedReport = { ...state.content_access_report }
            updatedReport.toolkit_copy_count = data.toolkit_copy_count > 0 ? data.toolkit_copy_count : updatedReport.toolkit_copy_count
            updatedReport.toolkit_print_count = data.toolkit_print_count > 0 ? data.toolkit_print_count : updatedReport.toolkit_print_count
            updatedReport.toolkit_resource_download_count = data.toolkit_resource_download_count > 0 ? data.toolkit_resource_download_count : updatedReport.toolkit_resource_download_count
            updatedReport.content_access_report_error = null
            commit('setContentAccessReport', updatedReport)
          }
        }, (error) => {
          const updatedReport = { ...state.content_access_report }
          updatedReport.content_access_report_error = error.message
          commit('setContentAccessReport', updatedReport)
        })
      }, 10000))
    }
  },
  getters: {
    copyQuotaExceeded(state) {
      return (
        state.content_access_report.toolkit_copy_count >=
        state.content_access_report.toolkit_copy_quota
      )
    },
    downloadQuotaExceeded(state) {
      return (
        state.content_access_report.toolkit_resource_download_count >=
        state.content_access_report.toolkit_resource_download_quota
      )
    },
    printQuotaExceeded(state) {
      return (
        state.content_access_report.toolkit_print_count >=
        state.content_access_report.toolkit_print_quota
      )
    },
    copyQuota(state) {
      return state.content_access_report.toolkit_copy_quota
    },
    copyCount(state) {
      return state.content_access_report.toolkit_copy_count
    },
    printQuota(state) {
      return state.content_access_report.toolkit_print_quota
    },
    printCount(state) {
      return state.content_access_report.toolkit_print_count
    },
    downloadQuota(state) {
      return state.content_access_report.toolkit_resource_download_quota
    },
    downloadCount(state) {
      return state.content_access_report.toolkit_resource_download_count
    },
    redownloadMap(state) {
      let map = {}
      state.downloaded_uuids.forEach(uuid => {
        map[uuid] = true
      })
      return map
    },
    contentAccessStatus(state, getters) {
      return {
        copyQuotaExceeded: getters.copyQuotaExceeded,
        downloadQuotaExceeded: getters.downloadQuotaExceeded,
        printQuotaExceeded: getters.printQuotaExceeded,
        redownloadMap: getters.redownloadMap,
        report: state.content_access_report,
        reportError: state.content_access_report_error
      }
    },
    maxStudentsReached(state, getters, rootState, rootGetters) {
      return (
        rootGetters['library/folderCount'] >= state.libraryQuotas.maxStudents &&
        state.libraryQuotas.shouldEnforce
      )
    },
    maxContentReachedForStudent(state, getters, rootState) {
      return (contentType, studentUuid) => {
        if (!state.libraryQuotas.shouldEnforce) {
          return false
        }
        let savedContentType = savedContentTypeMapping[contentType] || contentType
        let student = rootState.library.students.filter(s => s.uuid === studentUuid)[0]
        if (!student) {
          throw `Student ${studentUuid} was not found in user's students`
        }
        let count =
          student.content_counts && student.content_counts[savedContentType]
            ? student.content_counts[savedContentType]
            : 0
        if (
          [CONTENT_TYPES.SAVED_GOAL, CONTENT_TYPES.SAVED_SUPER_PAGE].indexOf(savedContentType) > -1
        ) {
          count =
            (student.content_counts[CONTENT_TYPES.SAVED_GOAL] || 0) +
            (student.content_counts[CONTENT_TYPES.SAVED_SUPER_PAGE] || 0)
        }
        let quota = getters.libraryQuotaForContentType(savedContentType)
        return count >= quota
      }
    },
    libraryQuotaForContentType(state) {
      return contentType => {
        switch (contentType) {
          case CONTENT_TYPES.SAVED_GOAL:
          case CONTENT_TYPES.SAVED_SUPER_PAGE:
            return state.libraryQuotas.maxGoalsPerStudent
          case CONTENT_TYPES.SAVED_STRATEGY:
            return state.libraryQuotas.maxStrategiesPerStudent
          case CONTENT_TYPES.PRESENTLEVEL:
            return state.libraryQuotas.maxPresentlevelsPerStudent
          case CONTENT_TYPES.RESOURCE:
            return state.libraryQuotas.maxResourcesPerStudent
        }
      }
    }
  }
}
